import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { CatalogsCheckoutModel, CatalogsCheckoutProduct } from "@/models/bricmate/catalogsCheckout";
import { AddOrUpdateCartItem, OrderRow } from "@/models/tapwell/Cart";
import { ApiService } from "@/services/bricmate/ApiService";
import { CheckoutStore } from "@/store/bricmate-b2c/checkoutstate";
import CheckoutCartWarning from "./CheckoutCartWarning.vue";

@Component({ components: { CheckoutCartWarning } })
export default class CatalogsCheckout extends Vue {
  @Prop()
  protected viewmodel!: CatalogsCheckoutModel;

  protected activeStep: number = 1;
  protected checkoutmodel!: CatalogsCheckoutModel;
  protected isBusy: boolean = true;
  protected error: string = "";
  protected translations: any = window.__litium.translation;

  get cart() {
    return CheckoutStore.cart;
  }

  get unexpectedRows(): OrderRow[] {
    const articles = this.checkoutmodel.catalogs.map((product) => product.articleNumber.toLowerCase());
    return (this.cart.orderRows || []).filter((row) => articles.indexOf(row.articleNumber.toLowerCase()) < 0);
  }

  get hasIncludedProducts(): boolean {
    return this.checkoutmodel.catalogs.some((product) => product.included);
  }

  get cartSignature(): string {
    return JSON.stringify((this.cart.orderRows || []).map((row) => [row.articleNumber, row.quantity]));
  }

  constructor() {
    super();
    this.checkoutmodel = this.viewmodel;
  }

  async mounted() {
    window.addEventListener("pageshow", this.onPageShow);
    try {
      await CheckoutStore.reloadState();
      this.syncIncludedProducts();
    } catch {
      this.error = this.translations["checkout.catalogs.error"];
    } finally {
      this.isBusy = false;
    }
  }

  beforeDestroy() {
    window.removeEventListener("pageshow", this.onPageShow);
  }

  private onPageShow(event: PageTransitionEvent) {
    if (!event.persisted) {
      return;
    }
    this.activeStep = 1;
    this.isBusy = true;
    this.clearWidget();
    window.location.reload();
  }

  @Watch("cartSignature")
  protected cartChanged() {
    this.syncIncludedProducts();
    this.activeStep = 1;
    this.clearWidget();
  }

  protected openStep(step: number) {
    if (this.isBusy || (step === 2 && (!this.hasIncludedProducts || this.unexpectedRows.length > 0))) {
      return;
    }
    this.activeStep = step;
    this.error = "";
    this.clearWidget();
    if (step === 2) {
      this.initializePaymentProvider();
    }
  }

  protected continueToCheckout() {
    this.openStep(2);
  }

  protected async toggleProduct(product: CatalogsCheckoutProduct) {
    if (this.isBusy) {
      return;
    }
    const orderRow = this.findOrderRow(product.articleNumber);
    const payload: AddOrUpdateCartItem = {
      articleNumber: product.articleNumber,
      quantity: product.included ? 0 : product.quantity || 1,
      rowSystemId: orderRow ? orderRow.rowSystemId : (null as any),
      comment: "",
    };
    this.isBusy = true;
    this.error = "";
    this.clearWidget();
    try {
      const response = orderRow ? await ApiService.updateCart(payload) : await ApiService.addToCart(payload);
      if (!response || !response.data) {
        throw new Error("Cart update failed");
      }
      await CheckoutStore.reloadState();
      this.syncIncludedProducts();
    } catch {
      this.error = this.translations["checkout.catalogs.error"];
    } finally {
      this.isBusy = false;
      this.$forceUpdate();
    }
  }

  private async initializePaymentProvider() {
    if (this.isBusy) {
      return;
    }
    this.isBusy = true;
    this.error = "";
    try {
      await CheckoutStore.reloadState();
      this.syncIncludedProducts();
      if (!this.hasIncludedProducts || this.unexpectedRows.length > 0) {
        this.activeStep = 1;
        return;
      }
      const response = await ApiService.setCatalogsPaymentProvider(this.checkoutmodel);
      if (!response || !response.data || !response.data.paymentWidget || !response.data.paymentWidget.responseString) {
        throw new Error("Payment initialization failed");
      }
      this.checkoutmodel = response.data;
      await this.$nextTick();
      if (this.activeStep === 2 && !this.unexpectedRows.length) {
        this.renderWidget();
        this.bindShippingOptionChange();
      }
    } catch {
      this.clearWidget();
      this.error = this.translations["checkout.catalogs.error"];
    } finally {
      this.isBusy = false;
    }
  }

  private renderWidget() {
    const checkoutContainer = document.getElementById("catalogs-checkout-widget");
    if (!checkoutContainer || !this.checkoutmodel.paymentWidget) {
      return;
    }
    checkoutContainer.innerHTML = this.checkoutmodel.paymentWidget.responseString || "";
    Array.from(checkoutContainer.getElementsByTagName("script")).forEach((script) => {
      const replacement = document.createElement("script");
      Array.from(script.attributes).forEach((attribute) => replacement.setAttribute(attribute.name, attribute.value));
      replacement.text = script.text;
      if (script.parentNode) {
        script.parentNode.replaceChild(replacement, script);
      }
    });
  }

  private bindShippingOptionChange() {
    const klarnaCheckout = (window as any)._klarnaCheckout;
    if (!klarnaCheckout) {
      throw new Error("Payment widget unavailable");
    }
    klarnaCheckout((api) => {
      api.on({
        shipping_option_change: async (data) => {
          if (this.activeStep !== 2 || this.unexpectedRows.length) {
            return;
          }
          try {
            this.checkoutmodel.klarnaSelectedShippingOption = data;
            const response = await ApiService.setCatalogsShippingOption(this.checkoutmodel);
            if (!response || !response.data) {
              throw new Error("Shipping update failed");
            }
            this.checkoutmodel = response.data;
            await CheckoutStore.reloadState();
          } catch {
            this.clearWidget();
            this.error = this.translations["checkout.catalogs.error"];
          }
        },
      });
    });
  }

  private clearWidget() {
    const checkoutContainer = document.getElementById("catalogs-checkout-widget");
    if (checkoutContainer) {
      checkoutContainer.innerHTML = "";
    }
  }

  private syncIncludedProducts() {
    this.checkoutmodel.catalogs.forEach((product) => {
      const orderRow = this.findOrderRow(product.articleNumber);
      product.included = !!orderRow;
      if (orderRow) {
        product.quantity = orderRow.quantity;
      }
    });
  }

  private findOrderRow(articleNumber: string): OrderRow | undefined {
    return (this.cart.orderRows || []).find((row) => row.articleNumber.toLowerCase() === articleNumber.toLowerCase());
  }
}